import posthog from 'posthog-js';

// ---------------------------------------------------------------------------
// Event type map — every valid Looma PostHog event (strategy v1.0)
// Vitrine events are intentionally excluded until online ordering is live.
// ---------------------------------------------------------------------------

type LoomaEventMap = {
  // Op Hub — Dashboard
  dashboard_opened:       { org_id: string; hour_of_day: number; day_of_week: number };
  alert_tapped:           { org_id: string; alert_type: 'low_stock' | 'unpaid_expense' | 'cash_discrepancy' };
  daily_close_initiated:  { org_id: string };
  daily_close_completed:  { org_id: string; discrepancy_amount: number; duration_seconds: number };
  // Op Hub — Catalogue
  item_created:           { org_id: string; has_photo: boolean; has_bom: boolean };
  first_item_created:     { org_id: string };
  item_photo_uploaded:    { org_id: string; item_id: string };
  // Op Hub — Ventes
  ventes_filter_used:     { org_id: string; filter_type: 'staff' | 'payment' | 'order_type' | 'date' };
  transaction_cancelled:  { org_id: string; reason: string; is_loss: boolean };
  // S3-11: manager-authorized post-hoc void (distinct from is_cancelled — see
  // void_transaction RPC truth model).
  transaction_voided:     { org_id: string; voided_by_staff_code_id: string };
  // Op Hub — Dépenses
  expense_created:        { org_id: string; method: 'manual' | 'ocr'; has_supplier: boolean };
  supplier_settled:       { org_id: string; supplier_id: string; amount: number };
  // Op Hub — QR Menu
  qr_menu_mode_changed:   { org_id: string; from: 'pdf' | 'catalogue'; to: 'pdf' | 'catalogue' };
  commander_link_copied:  { org_id: string; destination: 'whatsapp' | 'instagram' | 'clipboard' };
  // Op Hub — Accounting
  accounting_report_opened:    { org_id: string; report_type: 'trial_balance' | 'income_statement' | 'balance_sheet' | 'general_ledger' | 'cash_flow' | 'bank_reconciliation' };
  accounting_export_triggered: { org_id: string; report_type: string; format: 'pdf' | 'excel' };
  // POS Staff
  shift_opened:           { org_id: string; staff_code_id: string; opening_cash: number; auto_closed_count: number };
  shift_close_abandoned:  { org_id: string; staff_code_id: string };
  shift_closed:           { org_id: string; staff_code_id: string; discrepancy: number; duration_minutes: number; orders_count: number };
  cash_movement_recorded: { org_id: string; direction: 'in' | 'out'; reason: string };
  transaction_validated:  {
    org_id: string;
    amount: number;
    payment_method: string;
    order_source: string;
    order_type: string | null;
    items_count: number;
    draft_count: number;
    // STATE-GC-14 (required — STATE-GC-11 will replace null call-site values
    // with real expressions reading cart.draft.guestCount + derived time-to-payment)
    guest_count: number | null;
    time_to_payment_seconds: number | null;
  };
  first_transaction_validated: { org_id: string };
  // STATE-GC-14 — fired from handlePrintAddition in STATE-GC-12.
  bill_printed: {
    org_id: string;
    draft_order_id: string;
    table_id: string | null;
    table_label: string | null;
    items_count: number;
    total_amount: number;
    guest_count: number | null;
  };
  transaction_queued_offline:  { org_id: string; amount: number; payment_method: string };
  shift_open_abandoned:   { org_id: string; staff_code_id: string };
  shift_open_failed:      { org_id: string; staff_code_id: string; error: string };
  sync_failed:            { org_id: string; queued_count: number; error: string };
};

export type LoomaEventName = keyof LoomaEventMap;

export type FeatureFlag =
  | 'delivery_v2'
  | 'cancellation_flow'
  | 'qr_analytics_tab'
  | 'menu_engineering'
  | 'shift_analytics';

export interface GerantTraits {
  org_id: string;
  org_name: string;
  plan?: string;
  client_since?: string;
  role?: string;
}

// ---------------------------------------------------------------------------
// Module-level singleton — PostHog is initialized once.
// initAnalytics() is called from main.tsx before the React tree mounts.
// analyticsIdentify / analyticsReset are exported for use in AuthContext
// (async context — cannot call hooks there).
// ---------------------------------------------------------------------------

let _initialized = false;

export function initAnalytics(): void {
  if (_initialized) return;
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
  if (!apiKey) return; // silent no-op in local dev without the key
  posthog.init(apiKey, {
    api_host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com',
    loaded: (ph) => {
      if (import.meta.env.DEV) ph.debug(false);
    },
    capture_pageview: false, // explicit events only per strategy — no generic page_view
    persistence: 'localStorage+cookie',
  });
  _initialized = true;
}

export function analyticsCapture<E extends LoomaEventName>(
  event: E,
  properties: LoomaEventMap[E],
): void {
  if (!_initialized) return;
  posthog.capture(event, properties);
}

export function analyticsIdentify(userId: string, traits: GerantTraits): void {
  if (!_initialized) return;
  posthog.identify(userId, traits as unknown as Record<string, unknown>);
}

export function analyticsReset(): void {
  if (!_initialized) return;
  posthog.reset();
}

export function analyticsFeatureEnabled(flag: FeatureFlag): boolean {
  if (!_initialized) return false;
  return posthog.isFeatureEnabled(flag) ?? false;
}

// ---------------------------------------------------------------------------
// React hook — thin wrapper for use inside components
// ---------------------------------------------------------------------------

export function useAnalytics() {
  return {
    capture: analyticsCapture,
    identify: analyticsIdentify,
    reset: analyticsReset,
    isFeatureEnabled: analyticsFeatureEnabled,
  } as const;
}
